/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { MdDelete, MdEdit } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { formatDistance } from 'date-fns';

import {
    DialogContentText,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Switch,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
} from '@material-ui/core';

import DataTable from '~/components/DataTable';
import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';
import SimpleDialog from '~/components/SimpleDialog';

import api from '~/services/pluginbot-api';
import { expireSession } from '~/store/modules/auth/actions';
import getDateLocale from '~/util/GetDateLocale';

const DEFAULT_FORM = {
    name: '',
    model: '',
    provider: 'google_vertex',
    input_token_cost: '',
    output_token_cost: '',
    active: true,
};

function formatUsdPerMillion(value) {
    const amount = Number(value);
    if (value === null || value === undefined || Number.isNaN(amount)) {
        return '-';
    }

    const formatted = amount.toFixed(12).replace(/\.?0+$/, '') || '0';
    return `$ ${formatted} / 1M`;
}

function parseCostField(value) {
    if (value === '' || value === null || value === undefined) {
        return 0;
    }
    return Number(value);
}

function isValidCost(value) {
    return !Number.isNaN(value) && value >= 0;
}

export default function AiVersions() {
    const dispatch = useDispatch();
    const _settings = useSelector(state => state.settings || null);
    const date_loc = getDateLocale(_settings);
    const [isLoading, setIsLoading] = useState(true);
    const [versions, setVersions] = useState([]);

    const [formOpen, setFormOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [form, setForm] = useState(DEFAULT_FORM);

    function requestError(error) {
        if (error && error.response) {
            const code = error.response.data && error.response.data.code;
            const message = code ? (
                <LocaleMessage msg={`errors.${code}`} />
            ) : (
                <LocaleMessage msg="errors.unknown" />
            );
            toast.error(message);
            const { status } = error.response;
            if (status === 401) {
                dispatch(expireSession());
            }
        } else if (error && error.request) {
            toast.error(<LocaleMessage msg="errors.request" />);
        } else {
            toast.error(<LocaleMessage msg="errors.unknown" />);
        }
        setIsLoading(false);
    }

    async function loadItems() {
        try {
            setIsLoading(true);
            const response = await api.get('admin/ai_versions');
            if (response.status === 200 && response.data) {
                const data = response.data.map(v => {
                    const rawDate =
                        v.updated_at ||
                        v.updatedAt ||
                        v.created_at ||
                        v.createdAt;
                    const updated = rawDate ? new Date(rawDate) : null;
                    const hasValidDate =
                        updated instanceof Date &&
                        !Number.isNaN(updated.getTime());

                    const inputTokenCost =
                        v.input_token_cost !== null &&
                        v.input_token_cost !== undefined
                            ? Number(v.input_token_cost)
                            : null;
                    const outputTokenCost =
                        v.output_token_cost !== null &&
                        v.output_token_cost !== undefined
                            ? Number(v.output_token_cost)
                            : null;

                    return {
                        id: v.uid,
                        uid: v.uid,
                        name: v.name,
                        model: v.model,
                        provider: v.provider,
                        input_token_cost: formatUsdPerMillion(inputTokenCost),
                        output_token_cost: formatUsdPerMillion(outputTokenCost),
                        inputTokenCostRaw:
                            inputTokenCost === null ||
                            Number.isNaN(inputTokenCost)
                                ? ''
                                : String(inputTokenCost),
                        outputTokenCostRaw:
                            outputTokenCost === null ||
                            Number.isNaN(outputTokenCost)
                                ? ''
                                : String(outputTokenCost),
                        active: v.active ? (
                            <LocaleMessage msg="label.yes" />
                        ) : (
                            <LocaleMessage msg="label.no" />
                        ),
                        activeRaw: !!v.active,
                        updated: hasValidDate
                            ? formatDistance(updated, new Date(), {
                                  addSuffix: true,
                                  locale: date_loc,
                              })
                            : '-',
                        updated_timestamp: hasValidDate
                            ? updated.toISOString()
                            : '',
                    };
                });
                setVersions(data);
            } else {
                toast.error(<LocaleMessage msg="errors.permission" />);
            }
            setIsLoading(false);
        } catch (err) {
            requestError(err);
        }
    }

    useEffect(() => {
        loadItems();
    }, [date_loc]);

    function handleOpenAdd() {
        setSelectedVersion(null);
        setForm(DEFAULT_FORM);
        setFormOpen(true);
    }

    function handleOpenEdit(event, versionId) {
        if (event) event.preventDefault();
        const version = versions.find(v => v.uid === versionId);
        if (version) {
            setSelectedVersion(version);
            setForm({
                name: version.name || '',
                model: version.model || '',
                provider: version.provider || 'google_vertex',
                input_token_cost: version.inputTokenCostRaw || '',
                output_token_cost: version.outputTokenCostRaw || '',
                active: version.activeRaw,
            });
            setFormOpen(true);
        }
    }

    function handleOpenDelete(event, versionId) {
        if (event) event.preventDefault();
        setSelectedVersion(versions.find(v => v.uid === versionId) || null);
        setConfirmOpen(true);
    }

    async function handleSaveVersion(event) {
        if (event) event.preventDefault();
        if (
            !form.name ||
            !form.name.trim() ||
            !form.model ||
            !form.model.trim()
        ) {
            toast.error(<LocaleMessage msg="validation.required" />);
            return;
        }

        setIsLoading(true);
        try {
            const inputTokenCostValue = parseCostField(form.input_token_cost);
            const outputTokenCostValue = parseCostField(form.output_token_cost);

            if (
                !isValidCost(inputTokenCostValue) ||
                !isValidCost(outputTokenCostValue)
            ) {
                toast.error(
                    <LocaleMessage msg="page.ai_versions.token_cost_invalid" />
                );
                setIsLoading(false);
                return;
            }

            const payload = {
                name: form.name.trim(),
                model: form.model.trim(),
                provider: form.provider || 'google_vertex',
                input_token_cost: inputTokenCostValue,
                output_token_cost: outputTokenCostValue,
                active: !!form.active,
            };

            if (selectedVersion) {
                await api.put(
                    `admin/ai_versions/${selectedVersion.uid}`,
                    payload
                );
                toast.success(
                    <LocaleMessage msg="page.ai_versions.save_success" />
                );
            } else {
                await api.post('admin/ai_versions', payload);
                toast.success(
                    <LocaleMessage msg="page.ai_versions.create_success" />
                );
            }
            setFormOpen(false);
            loadItems();
        } catch (err) {
            requestError(err);
        }
    }

    async function handleDeleteVersion() {
        if (!selectedVersion) return;
        setIsLoading(true);
        setConfirmOpen(false);
        try {
            await api.delete(`admin/ai_versions/${selectedVersion.uid}`);
            toast.success(
                <LocaleMessage msg="page.ai_versions.delete_success" />
            );
            loadItems();
        } catch (err) {
            requestError(err);
        }
    }

    function buildListView() {
        const headCells = [
            { id: 'name', label: <LocaleMessage msg="table.headers.name" /> },
            {
                id: 'model',
                label: <LocaleMessage msg="table.headers.model" />,
            },
            {
                id: 'provider',
                label: <LocaleMessage msg="table.headers.provider" />,
            },
            {
                id: 'input_token_cost',
                label: (
                    <LocaleMessage msg="page.ai_versions.label.input_token_cost_short" />
                ),
            },
            {
                id: 'output_token_cost',
                label: (
                    <LocaleMessage msg="page.ai_versions.label.output_token_cost_short" />
                ),
            },
            {
                id: 'active',
                label: <LocaleMessage msg="table.headers.status" />,
            },
            {
                id: 'updated',
                label: <LocaleMessage msg="table.headers.updated" />,
                order_by: 'updated_timestamp',
            },
        ];

        const rowActions = [
            {
                id: 'edit',
                label: <LocaleMessage msg="button.edit" />,
                icon: <MdEdit />,
                action: (event, id) => handleOpenEdit(event, id),
            },
            {
                id: 'delete',
                label: <LocaleMessage msg="button.delete" />,
                icon: <MdDelete />,
                action: (event, id) => handleOpenDelete(event, id),
            },
        ];

        return (
            <div style={{ minHeight: '150px', width: '100%', padding: '15px' }}>
                <DataTable
                    headerColumns={headCells}
                    data={versions}
                    orderColumn="name"
                    rowActions={rowActions}
                />
            </div>
        );
    }

    return (
        <PageContent
            title={<LocaleMessage msg="page.ai_versions.title" />}
            loading={isLoading}
        >
            <>
                <div
                    className="body-top-controls mb-3"
                    style={{ marginBottom: '20px' }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenAdd}
                    >
                        <LocaleMessage msg="page.ai_versions.button.new" />
                    </Button>
                </div>
                {buildListView()}

                <Dialog
                    open={formOpen}
                    onClose={() => setFormOpen(false)}
                    aria-labelledby="ai-version-form-dialog-title"
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle id="ai-version-form-dialog-title">
                        {selectedVersion ? (
                            <LocaleMessage msg="page.ai_versions.edit_title" />
                        ) : (
                            <LocaleMessage msg="page.ai_versions.create_title" />
                        )}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="name"
                            label={<LocaleMessage msg="table.headers.name" />}
                            type="text"
                            fullWidth
                            value={form.name}
                            onChange={e =>
                                setForm({ ...form, name: e.target.value })
                            }
                        />
                        <TextField
                            margin="dense"
                            id="model"
                            label={<LocaleMessage msg="table.headers.model" />}
                            type="text"
                            fullWidth
                            value={form.model}
                            onChange={e =>
                                setForm({ ...form, model: e.target.value })
                            }
                            helperText={
                                <LocaleMessage msg="page.ai_versions.model_hint" />
                            }
                        />
                        <FormControl fullWidth margin="dense">
                            <InputLabel>
                                <LocaleMessage msg="table.headers.provider" />
                            </InputLabel>
                            <Select
                                value={form.provider || 'google_vertex'}
                                onChange={e =>
                                    setForm({
                                        ...form,
                                        provider: e.target.value,
                                    })
                                }
                            >
                                <MenuItem value="google_vertex">
                                    google_vertex
                                </MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            margin="dense"
                            id="input_token_cost"
                            label={
                                <LocaleMessage msg="page.ai_versions.label.input_token_cost" />
                            }
                            type="number"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        USD $
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        / 1M
                                    </InputAdornment>
                                ),
                            }}
                            value={form.input_token_cost}
                            onChange={e =>
                                setForm({
                                    ...form,
                                    input_token_cost: e.target.value,
                                })
                            }
                            helperText={
                                <LocaleMessage msg="page.ai_versions.token_cost_hint" />
                            }
                        />
                        <TextField
                            margin="dense"
                            id="output_token_cost"
                            label={
                                <LocaleMessage msg="page.ai_versions.label.output_token_cost" />
                            }
                            type="number"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        USD $
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        / 1M
                                    </InputAdornment>
                                ),
                            }}
                            value={form.output_token_cost}
                            onChange={e =>
                                setForm({
                                    ...form,
                                    output_token_cost: e.target.value,
                                })
                            }
                            helperText={
                                <LocaleMessage msg="page.ai_versions.token_cost_hint" />
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!form.active}
                                    onChange={e =>
                                        setForm({
                                            ...form,
                                            active: e.target.checked,
                                        })
                                    }
                                    color="primary"
                                />
                            }
                            label={
                                <LocaleMessage msg="page.ai_versions.label.active" />
                            }
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setFormOpen(false)}
                            color="secondary"
                        >
                            <LocaleMessage msg="button.cancel" />
                        </Button>
                        <Button onClick={handleSaveVersion} color="primary">
                            <LocaleMessage msg="button.save" />
                        </Button>
                    </DialogActions>
                </Dialog>

                <SimpleDialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    title={
                        <LocaleMessage msg="page.ai_versions.delete_title" />
                    }
                    content={
                        <DialogContentText>
                            <LocaleMessage msg="message.undone.content" />
                        </DialogContentText>
                    }
                    actions={[
                        {
                            key: 'cancel',
                            onClick: () => setConfirmOpen(false),
                            label: <LocaleMessage msg="button.cancel" />,
                        },
                        {
                            key: 'remove',
                            onClick: handleDeleteVersion,
                            label: <LocaleMessage msg="button.delete" />,
                        },
                    ]}
                />
            </>
        </PageContent>
    );
}
